'use client';

import Image from 'next/image';

import { useNearViewport } from '@eliadmin/core/src/hooks/use-near-viewport';

import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { SectionHeading } from '../ui/section-heading';
import { FaStar } from 'react-icons/fa';
import { Button } from '@eliadmin/core/src/components/ui/button';
import Link from '@eliadmin/core/src/components/link';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { fetchData } from '@eliadmin/core/src/api/fetcher';
import { Review } from '@eliadmin/core/src/api/general/general-api';

export function Reviews() {
  const locale = useLocaleStore(s => s.currentLocale);
  const { ref, enabled } = useNearViewport();

  const { data: reviews = { data: [] } } = useQuery({
    enabled,
    queryKey: ['REVIEWS', locale],
    queryFn: () =>
      fetchData<{ data: Review[] }>({
        locale,
        method: 'GET',
        uri: 'store/reviews',
      }),
  });

  const LOOPED_REVIEWS = [...reviews.data, ...reviews.data];

  return (
    <section ref={ref} className={cn('custom-section', 'py-[60px]', 'generic-gap')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-4 md:flex-row md:items-end md:justify-between',
        )}
      >
        <SectionHeading
          align="left"
          title="<span>Dôvera pacientov</span> je pre nás najviac"
          description="Reálne hodnotenia od reálnych pacientov. Profesionálny prístup, ochotný personál a rýchle vybavenie sú u nás samozrejmosťou."
          className={{
            container: 'w-full max-w-[588px]',
            title: { container: 'w-full max-w-[450px]', title: 'text-balance' },
            description: 'w-full',
          }}
        />
        <div className="relative z-10 flex w-full min-w-[282px] max-w-[282px] flex-col items-start justify-start gap-2 rounded-[24px] bg-[#F4F7FB] p-5">
          <div className="flex w-full items-start justify-between">
            <p className={cn('h2', 'font-heading font-semibold tracking-tight text-secondary')}>
              4.9<span className="text-lg">/&nbsp;5</span>
            </p>
            <Image
              src={'/images/google.svg'}
              alt="Google"
              width={24}
              height={24}
              className="shrink-0 object-contain"
            />
          </div>
          <div className="flex items-center justify-start gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <FaStar key={i} size={20} className="text-[#F7C661]" />
            ))}
          </div>
          <Paragraph size="base">z 320+ Google hodnotení</Paragraph>
        </div>
      </div>

      {/* full-bleed: negate the px-4 from custom-section */}
      <div className="ea-reviews-carousel -mx-4 min-h-[424px] w-[calc(100%+2rem)] overflow-hidden pb-16">
        <div className="ea-reviews-carousel__track flex gap-4 motion-reduce:!animate-none sm:gap-5 lg:gap-6">
          {LOOPED_REVIEWS.map((review, i) => (
            <ReviewCard review={review} key={`${review.name}-${i}`} />
          ))}
        </div>
      </div>

      <Button asChild variant={'outline'} className="-mt-16">
        <Link
          prefetch={false}
          href={
            'https://www.google.com/search?sca_esv=20b4b65056ec4911&cs=1&sxsrf=APpeQnue9rSALPWTpL2rQjl4p32hS7SXew:1783938605625&q=Pohotovostn%C3%A1%20lek%C3%A1re%C5%88%20TaJ%20Sek%C4%8Dov%20Recenzie&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2sjCxMLY0MjY3MTM0MTIyMbEw2MDI-IpRJyA_I78kvyy_uCTv8EKFnNTswwuLUo92KIQkeikEp2Yf6c0vUwhKTU7Nq8pMXcRKknIAD_DQD3gAAAA&rldimm=2848392374614224480&tbm=lcl&hl=sk-SK&sa=X&ved=0CAcQ5foLahcKEwig_9iSuc-VAxUAAAAAHQAAAAAQJA#lkt=LocalPoiReviews&arid=Ci9DQUlRQUNvZENodHljRjlvT2paQlNpMXVVRlJFVUU1TmQwdHZkak5XUTFOTlVHYxAB'
          }
          target="_blank"
        >
          <Image
            src={'/images/google.svg'}
            alt="Google"
            width={24}
            height={24}
            className="shrink-0 object-contain"
          />
          Všetky recenzie
        </Link>
      </Button>
    </section>
  );
}

function ReviewCard({ review }: { review: Review }) {
  return (
    <div
      className={cn(
        'flex h-[360px] w-[282px] shrink-0 flex-col items-start justify-between rounded-[24px] border p-6',
        'ea-review-card',
        'ea-generic-card-shadow',
      )}
    >
      <div className="flex w-full flex-col items-start justify-start gap-5">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center justify-start gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <FaStar key={i} size={20} className="text-[#F7C661]" />
            ))}
          </div>
          <Image
            src={'/images/google.svg'}
            alt="Google"
            width={24}
            height={24}
            className="shrink-0 object-contain"
          />
        </div>
        <Paragraph
          innerHtml={review.review}
          className={cn('ea-review-card__review', 'line-clamp-6 w-full')}
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-1">
        <p className="text-base font-semibold text-secondary">{review.name}</p>
      </div>
    </div>
  );
}
